import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const CHAPTER_LABELS = {
  c0: "Cover & How to Use", c1: "01 Company & Governance", c2: "02 Compliance Program",
  c3: "03 Regulatory Framework", c4: "04 FWA & Claims Integrity", c5: "05 Exclusion Screening",
  c6: "06 HIPAA Privacy & Security", c7: "07 Credentialing & Enrollment", c8: "08 Recruiting & Classification",
  c9: "09 Workforce Safety (OSHA)", c10: "10 Facility & Clinical Engagement", c11: "11 Case Intake & Eligibility",
  c12: "12 Documentation & Evidence", c13: "13 Billing, RCM & Vendor Oversight", c14: "14 Reporting Framework",
  c15: "15 Finance & Payroll", c16: "16 Audit & QA", c17: "17 Patient Communications",
  c18: "18 Government Inquiries", c19: "19 Continuity & Incidents", c21: "20 Monthly Review (MOCR)",
  c20: "21 Appendices & Crosswalk", c22: "[VERIFY] Register",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" }) : "—");

function PersonDetail({ person, chapters }) {
  return (
    <tr>
      <td colSpan={4} style={{ background: "var(--paper, #fafaf7)" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 24px", padding: "8px 4px" }}>
          {chapters.map((cid) => {
            const c = person.confirmed[cid];
            return (
              <div key={cid} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, borderBottom: "1px dotted var(--line, #ddd)", padding: "3px 0" }} data-testid={`rdc-row-${person.email}-${cid}`}>
                <span>{c ? "✓" : "✗"} {CHAPTER_LABELS[cid] || cid}</span>
                <span className="cap-sub">{c ? `${fmt(c.confirmed_at)} · v${c.manual_version}` : "unread"}</span>
              </div>
            );
          })}
        </div>
      </td>
    </tr>
  );
}

export default function ReadCompliance({ active }) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);

  const fetchData = useCallback(() => {
    axios.get(`${API}/read-compliance`).then((r) => setData(r.data)).catch(() => setData({ people: [], required_chapters: [] }));
  }, []);
  useEffect(() => { if (active === "rdc") fetchData(); }, [active, fetchData]);

  const people = data?.people || [];
  const done = people.filter((p) => p.confirmed_count === p.total_required).length;

  return (
    <section className={`chapter utility${active === "rdc" ? " active" : ""}`} id="rdc">
      <div className="eyebrow">Working record · SOP-1.1 evidence — who has actually read what, per person</div>
      <h1>Read Compliance</h1>
      <p className="lede">Pick a name to see every chapter they've confirmed, when, against which version — and what's still unread. The SOP-1.1 sign-off stays gated until all {data ? data.required_chapters.length : 23} sections are confirmed, and draft-version reads will need re-acknowledgment at FINAL.</p>

      <table data-testid="rdc-table">
        <tbody>
          <tr><th>Team member</th><th>Progress</th><th>Sign-off (SOP-1.1)</th><th style={{ width: 120 }}></th></tr>
          {data === null ? (
            <tr><td colSpan={4}>Loading…</td></tr>
          ) : people.length === 0 ? (
            <tr><td colSpan={4}>No registered users yet.</td></tr>
          ) : (
            people.map((p) => {
              const complete = p.confirmed_count === p.total_required;
              const ack = p.latest_ack;
              return (
                <React.Fragment key={p.user_id}>
                  <tr>
                    <td>{p.name}<div className="cap-sub">{p.email}</div></td>
                    <td>
                      <span className={`vr-pill ${complete ? "cleared" : "open"}`} data-testid={`rdc-progress-${p.email}`}>{p.confirmed_count} / {p.total_required}</span>
                      {!complete && <div className="cap-sub">{p.missing.length} unread</div>}
                    </td>
                    <td>
                      {ack ? (
                        <>
                          <span className={`vr-pill ${String(ack.manual_version || "").endsWith("-D") ? "open" : "cleared"}`} data-testid={`rdc-ack-${p.email}`}>
                            SIGNED v{ack.manual_version}{String(ack.manual_version || "").endsWith("-D") ? " (DRAFT READ)" : ""}
                          </span>
                          <div className="cap-sub">{fmt(ack.acknowledged_at)}</div>
                        </>
                      ) : (
                        <span className="cap-sub" data-testid={`rdc-ack-${p.email}`}>not signed{complete ? " — eligible now" : " — gated"}</span>
                      )}
                    </td>
                    <td><button className="vr-link" onClick={() => setOpenId(openId === p.user_id ? null : p.user_id)} data-testid={`rdc-toggle-${p.email}`}>{openId === p.user_id ? "Hide chapters" : "View chapters"}</button></td>
                  </tr>
                  {openId === p.user_id && <PersonDetail person={p} chapters={data.required_chapters} />}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
      {data && people.length > 0 && (
        <p style={{ color: "var(--mute)", fontSize: "13px" }} data-testid="rdc-summary">
          {done} of {people.length} team member{people.length === 1 ? "" : "s"} fully read · current manual v{data.manual_version}. Every confirmation here is also a timestamped event in the Audit Trail and exports with the binder.
        </p>
      )}
    </section>
  );
}
